import qs from 'qs';
import { useCallback, useState } from 'react';
import { ZodSchema } from 'zod';

/**
 * Max length of parsed array from URL
 * @see https://github.com/ljharb/qs?tab=readme-ov-file#parsing-arrays
 */
export const MAX_ARRAY_ITEMS_PARSE_COUNT = Infinity;

const parseQuery = () => {
    const url = new URL(window.location.href);
    return qs.parse(url.search, { ignoreQueryPrefix: true, allowDots: true, arrayLimit: MAX_ARRAY_ITEMS_PARSE_COUNT });
};

const setQuery = (data: unknown) => {
    const url = new URL(window.location.href);
    url.search = qs.stringify(data, { allowDots: true });
    window.history.replaceState(window.history.state, '', url);
};

/**
 * Parses values from URL search query accordingly to a Zod `schema` and provides a method for syncing them back on change.
 *
 * The values are parsed only once, during a first call. Values not found in the schema are ignored.
 *
 * If the schema fails to match values in the URL, it will fail silently and return an empty object instead.
 *
 * To get parse a value as a different type than string use `coerce` on Zod schema.
 * Note that Boolean coercion is done with `Boolean(value)` so a string "false" is parsed as true,
 * while "0" would be parsed as false.
 *
 * - @param schema Zod schema to match against the URL search query values.
 * @returns Parsed URL search query values or `{}` on failure.
 */
export const useSafeUrlState = <T>(schema: ZodSchema<T>): [T | undefined, (nextState: T) => void] => {
    const isDevelop = process.env.NODE_ENV === 'development';
    const [initialState] = useState(() => {
        const result = schema.safeParse(parseQuery());

        if (!result.success) {
            if (isDevelop) {
                // eslint-disable-next-line no-console
                console.error('Parsing url state error', result);
            }
            return undefined;
        }

        return result.data;
    });

    const handleSetState = useCallback((nextState: T) => {
        setQuery(nextState);
    }, []);

    return [initialState, handleSetState];
};
